import { SelectOption } from "atoms/forms/select";
import { NotebookResourceType } from "models/enumerations/notebooks/notebook-resource-type";
import { RecipientType } from "models/enumerations/notebooks/recipient-type";
import NotebookPermissionManifestRecord from "models/view-models/notebooks/notebook-permission-manifest-record";
import NotebookPermissionRecipientRecord from "models/view-models/notebooks/notebook-permission-recipient-record";
import NotebookResourcePermissionRecord from "models/view-models/notebooks/notebook-resource-permission-record";
import { FilterOptionOption, InputActionMeta } from "react-select";

export type NotebookPermissionSelectOption = SelectOption<
    NotebookPermissionManifestRecord,
    number
>;

interface Options {
    currentlySelectedOptions: NotebookPermissionManifestRecord[];
    persistedValues: NotebookPermissionManifestRecord[];
    availableRecipients: NotebookPermissionRecipientRecord[];
    setInputValue: React.Dispatch<React.SetStateAction<string>>;
}

const useNotebookSharingDropdownOptions = ({
    currentlySelectedOptions,
    persistedValues,
    availableRecipients,
    setInputValue,
}: Options) => {
    const recipientManifests = availableRecipients?.map(
        (recipient) =>
            new NotebookPermissionManifestRecord({
                recipient,
                permission: new NotebookResourcePermissionRecord({
                    resourceType: NotebookResourceType.Notebook,
                    recipientId: recipient.id,
                    recipientType: recipient.type,
                }),
            })
    );

    const buildOptionsList = (): NotebookPermissionSelectOption[] => {
        const availableOptions =
            recipientManifests?.filter(
                (manifest) =>
                    !currentlySelectedOptions.some(
                        (selected) =>
                            selected.recipient?.id === manifest.recipient?.id &&
                            selected.recipient?.type ===
                                manifest.recipient?.type
                    ) &&
                    !persistedValues.some(
                        (persisted) =>
                            persisted.recipient?.id ===
                                manifest.recipient?.id &&
                            persisted.recipient?.type ===
                                manifest.recipient?.type
                    )
            ) ?? [];

        return availableOptions?.sort(sortByRecipientType).map((option) => ({
            value: option.recipient?.id ?? 0,
            label: option.recipient?.name ?? "",
            data: option,
        }));
    };

    const optionFilter = (
        option: FilterOptionOption<NotebookPermissionSelectOption>,
        inputValue: string
    ) => {
        const name = option.data?.data?.recipient.name ?? "";
        const email = option.data?.data?.recipient.email ?? "";

        return (
            name.toLowerCase().includes(inputValue.toLowerCase()) ||
            email.toLowerCase().includes(inputValue.toLowerCase())
        );
    };

    const handleSearchChange = (
        newValue: string,
        actionMeta?: InputActionMeta
    ) => {
        // Only update the input value when the user is typing, not when an option is selected
        if (actionMeta?.action === "input-change") {
            setInputValue(newValue);
            return newValue;
        }

        // For other actions (like selecting an option), keep the current input value
        return actionMeta?.prevInputValue ?? newValue;
    };

    const options: NotebookPermissionSelectOption[] = buildOptionsList();
    const value = currentlySelectedOptions.map(
        (option) => option.recipient?.id ?? 0
    );
    return {
        options,
        value,
        // To fit the type expected by MultiSelect
        optionFilter: optionFilter as (
            option: FilterOptionOption<unknown>,
            inputValue: string
        ) => boolean,
        handleSearchChange,
    };
};

export default useNotebookSharingDropdownOptions;

const sortOrder = {
    [RecipientType.WorkGroup]: 1,
    [RecipientType.User]: 2,
    [RecipientType.Group]: 3,
};

const sortByRecipientType = (
    a: NotebookPermissionManifestRecord,
    b: NotebookPermissionManifestRecord
) => {
    const aType = a.recipient?.type ?? 0;
    const bType = b.recipient?.type ?? 0;

    const typeCompare = sortOrder[aType] - sortOrder[bType];

    if (typeCompare !== 0) return typeCompare;

    return (a.recipient?.name ?? "").localeCompare(b.recipient?.name ?? "");
};
